import { Component, OnInit } from '@angular/core';

import { GrantService } from '../../authentication/grant.service';
import { Grant } from '../../authentication/grant';
import { PermissionEnum } from '../../authentication/permission-enum';
import { ErrorHandler } from '../../global/error-handling/error-handler';

/**
 * This is the base layout component used for authenticated users.
 */
@Component({
	selector: 'cc-base-nav-layout',
	templateUrl: './base-nav-layout.component.html',
	styleUrls: ['./base-nav-layout.component.css']
})
export class BaseNavLayoutComponent implements OnInit {

// #region [ Fields ]

	/** This model holds the grants that have been issued and need to be acknowledged. */
	public IssuedGrantsModel: Grant[] = null;

// #region [ Constructor ]

	constructor(
		private _GrantService : GrantService, private _ErrorHandler: ErrorHandler
	) { }

// #region [ Implemented Methods ]

	/**
	 * Component initialization.
	 */
	ngOnInit(): void {
		this._GrantService.GetIssuedGrants()
			.then((objGrants: Grant[]) => {
				this.IssuedGrantsModel = objGrants;
			})
			.catch(() => {
				this._ErrorHandler.ShowError(null, "Could not load permissions from the server; please try again.");
			});
	}

// #region [ Properties ]

	public get ShowGrantsDialog(): boolean {
		return this.HasUnacknowledgedGrants();
	}

// #region [ Event Handlers ]

	/**
	 * Handles the user clicking an accept button for an issued set of grants.
	 *
	 * @param CrownIdentifier The Crown ID of the issuing company.
	 * @param Permission The permission of the issued grant(s).
	 */
	public OnAcceptButtonClick(CrownIdentifier: string, Permission: PermissionEnum): void {
		let objGrants: Grant[];

		this._GrantService.AcceptGrants(CrownIdentifier, Permission)
			.then(() => {
				objGrants = this.IssuedGrantsModel.filter(objG => objG.CrownIdentifier == CrownIdentifier && objG.Permission == Permission);
				objGrants[0].Acknowledged = true;
				if (!this.HasUnacknowledgedGrants())
					this._GrantService.GetAcceptedGrants();
			})
			.catch(() => {
				this._ErrorHandler.ShowError(null, "Could not accept the grants; please try again.");
			});
	}

	/**
	 * Handles the user clicking a reject button for an issued set of grants.
	 *
	 * @param CrownIdentifier The Crown ID of the issuing company.
	 * @param Permission The permission of the issued grant(s).
	 */
	public OnRejectButtonClick(CrownIdentifier: string, Permission: PermissionEnum): void {
		let objGrants: Grant[];

		this._GrantService.RejectGrants(CrownIdentifier, Permission)
			.then(() => {
				objGrants = this.IssuedGrantsModel.filter(objG => objG.CrownIdentifier == CrownIdentifier && objG.Permission == Permission);
				objGrants[0].Acknowledged = true;
				if (!this.HasUnacknowledgedGrants())
					this._GrantService.GetAcceptedGrants();
			})
			.catch(() => {
				this._ErrorHandler.ShowError(null, "Could not reject the grants; please try again.");
			});
	}

// #region [ Public Methods ]

	/**
	 * Gets the human readable text associated with a permission.
	 *
	 * @param Permission The permission to get the text of.
	 * @returns A human readable description of the permission.
	 */
	public GetPermissionText(Permission: PermissionEnum): string {
		switch (Permission) {
			case PermissionEnum.QuoteShipments:
				return "Quote Shipments";
			case PermissionEnum.TrackShipments:
				return "Read Shipment Data";
			case PermissionEnum.BookQuotes:
				return "Book Quotes";
			default:
				return Permission;
		}
	}

// #region [ Private Methods ]

	/**
	 * Indicates if the overlay and grant acknowledgement dialog should be shown.
	 * The dialog is shown when there are issued grants which haven't been acknowledged.
	 *
	 * @returns A boolean indicating if the dialog should be shown to the user.
	 */
	private HasUnacknowledgedGrants(): boolean {
		let bolAnyUnacknowledgedLeft, bolAllAcknowledged: boolean;

		bolAnyUnacknowledgedLeft = (this.IssuedGrantsModel != null);
		if (bolAnyUnacknowledgedLeft) {
			bolAllAcknowledged = true;
			this.IssuedGrantsModel.forEach(objG => bolAllAcknowledged = bolAllAcknowledged && objG.Acknowledged)
			bolAnyUnacknowledgedLeft = bolAnyUnacknowledgedLeft && !bolAllAcknowledged;
		}

		return bolAnyUnacknowledgedLeft;
	}

}